/**
 * The sidebar, as data.
 *
 * `needs` is ours, not the shell's: `[resource, verb]`, matching the matrix
 * access-management actually speaks. An item whose verb the user does not hold
 * is **dropped**, not disabled — a disabled item advertises a capability the
 * user cannot obtain and generates a support ticket (FR-24).
 *
 * **What is on the rail is what has a service route** (plan §7): Overview,
 * Documents, Upload, Review for whoever decides one (W4), Workspaces for
 * whoever may make one, Tags for whoever manages them (W5), and Document
 * settings for everyone who may read them (W6, `settings:can_view` — only
 * `settings:can_update` changes them). Folders are not rail items of their
 * own; nor is *Shared with me*. Engine W15 adds
 * **Extractions** — what the engine read and a person must check — for
 * whoever corrects and decides one (`extraction:can_update`, FR-E20).
 * The prototype's tasks, calendar, automations and messaging screens are never
 * ProDoc's and are gone (ADR-0011, T3.1).
 *
 * Kept in its own module so the test can read the list without rendering the
 * shell.
 *
 * **W22 (FR-59, D57): *Upload* is an act, not a page.** It opens the upload
 * popup over whatever screen is showing, so `renderNavItem` draws it as a
 * `<button>` wearing the class the shell hands every item (`navItemClass`) —
 * the element chosen by what it does, the class by the surface it sits on
 * (UI_SYSTEM §2, as `renderBrand`; `.sweep-allow` says so). Its `href` stays
 * `/upload`, which still answers: it opens Documents with the popup open.
 *
 * **W25 (platform FR-24b, FR-24c, FR-60, D76).** The rail folds to its icons
 * (`<PlatformShell collapsible>`), and folded **each item is its icon alone**,
 * its name in a tooltip — so every item here carries one, or folded it is an
 * empty square. The shell folds what `renderNavItem` draws from the outside,
 * the *Upload* button as well as the links. And **the folders live under
 * Documents**: the item's `panel` is the package's folder tree
 * (`components/folder-menu.tsx`), handed in through `navFor`'s `panels`.
 */
import { navItemClass, type NavItem, type RenderLink } from '@fiveox/shell'
import { Building2, ClipboardCheck, CloudUpload, FileText, LayoutDashboard, ScanText, Settings, Tags } from 'lucide-react'
import type { ReactNode } from 'react'
import { NavLink } from 'react-router'

import { DOCUMENT, EXTRACTION, SETTINGS, TAG, WORKSPACE, type Verb } from './lib/access'

const ICON = { size: 18, strokeWidth: 1.75 } as const

export type AppNavItem = NavItem & { needs?: [string, Verb] }

export const NAV: AppNavItem[] = [
  { key: 'overview', label: 'Overview', href: '/', icon: <LayoutDashboard {...ICON} />, end: true },
  {
    key: 'documents',
    label: 'Documents',
    href: '/documents',
    icon: <FileText {...ICON} />,
    needs: [DOCUMENT, 'can_view'],
  },
  {
    key: 'upload',
    label: 'Upload',
    href: '/upload',
    icon: <CloudUpload {...ICON} />,
    needs: [DOCUMENT, 'can_create'],
  },
  {
    // Approving and rejecting is `can_update` on `document` (plan §5.2) —
    // `reviewer` and `admin`. An uploader's own-upload edits ride `can_create`
    // and never reach this page (D12).
    key: 'review',
    label: 'Review',
    href: '/review',
    icon: <ClipboardCheck {...ICON} />,
    needs: [DOCUMENT, 'can_update'],
  },
  {
    // Engine W15 (FR-E20): what the engine read and a person checks — correcting,
    // accepting and rejecting is `extraction:can_update`, `reviewer` and `admin`.
    // Reading one is the document's own Extractions tab, for every role.
    key: 'extractions',
    label: 'Extractions',
    href: '/extractions',
    icon: <ScanText {...ICON} />,
    needs: [EXTRACTION, 'can_update'],
  },
  {
    // Workspaces are made, renamed and archived by whoever holds `can_create`
    // on `workspace` — `admin` in plan §4.4. Members and roles are the
    // organisation's and live in the FiveOX console; the page links out.
    key: 'workspaces',
    label: 'Workspaces',
    href: '/admin/workspaces',
    icon: <Building2 {...ICON} />,
    needs: [WORKSPACE, 'can_create'],
  },
  {
    // Managing the organisation's tags — rename, recolour, merge, delete — is
    // `can_update` on `tag` (FR-14, §9-17: the administrator's). Uploaders and
    // reviewers make tags while tagging, from the document, not from here.
    key: 'tags',
    label: 'Tags',
    href: '/admin/tags',
    icon: <Tags {...ICON} />,
    needs: [TAG, 'can_update'],
  },
  {
    // Document types, review defaults, retention above the statute's floors and
    // the link defaults (FR-36), with the console's link-outs for what is not
    // ProDoc's (FR-37). Every role reads them; `settings:can_update` changes them.
    // W28 (FR-65): a hub with a page per area, `/settings/<area>` — no `end`, so
    // the item stays current on every page under it.
    key: 'settings',
    label: 'Settings',
    href: '/settings',
    icon: <Settings {...ICON} />,
    needs: [SETTINGS, 'can_view'],
  },
]

/**
 * The rail for this person: every item whose verb they hold, `needs` stripped —
 * and, under an item, the panel the host hands in by its key (W25: the folder
 * tree under `documents`). A `null` panel is no panel: the item stays a link.
 */
export function navFor(can: (resource: string, verb: Verb) => boolean, panels: Partial<Record<string, ReactNode>> = {}): NavItem[] {
  return NAV.filter((item) => !item.needs || can(item.needs[0], item.needs[1])).map(({ needs: _needs, ...item }) => {
    const panel = panels[item.key]
    return panel === undefined || panel === null || panel === false ? item : { ...item, panel }
  })
}

/** The item whose panel is the folder tree (FR-60). */
export const DOCUMENTS_ITEM = 'documents'

/** The rail item that opens the upload popup rather than a page. */
export const UPLOAD_ITEM = 'upload'

/**
 * Each rail item in react-router's `<NavLink>`, which decides its active state
 * — and *Upload* as a button that calls `onUpload` (FR-59). The shell's
 * `className` is the inactive variant, which is what a button that never
 * becomes the current page wears.
 */
export function renderNavItem(onUpload: () => void): RenderLink {
  return (item, className) =>
    item.key === UPLOAD_ITEM ? (
      <button type="button" className={`${className} w-full text-left`} onClick={onUpload} aria-haspopup="dialog">
        {item.icon}
        {item.label}
      </button>
    ) : (
      <NavLink to={item.href} end={item.end} className={({ isActive }) => navItemClass(isActive)}>
        {item.icon}
        {item.label}
      </NavLink>
    )
}
